/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 */
package analytics

import kotlinx.serialization.Serializable

/**
 * The one place that decides which transaction rows may contribute to a
 * monthly total, and what they contribute to.
 *
 * This existed once per platform (web's `computeMonthlyAggregates`, iOS's
 * `calculateMonthlyFinanceData`, Android's `calculateMonthlyData` +
 * `computeGoalAllocationsForYear`) and drifted: Android's goal-allocation copy
 * never applied the exclusion rule, so a split parent's allocation was counted
 * on top of its children's and May's savings rate read 37.8% where web and iOS
 * both read 26.0%. Same shape of bug as the per-platform category palettes and
 * the triplicated bucket-override precedence before it. The rule now lives here
 * and the platforms only adapt their row type to [TxnLike].
 *
 * Pure function, no I/O. Mirrors [ai.rules.BudgetHealthAggregator]'s shape:
 * a minimal `TxnLike` each platform implements, one entry point per call shape.
 *
 * ## The three rules
 *
 * 1. An [TxnLike.excluded] row (split parent / distributed parent / explicitly
 *    excluded — the shared `Transaction.excludeFromTotals`) contributes to
 *    **nothing**: not income, not expenses, and **not goal allocations**. A
 *    split or distributed PARENT carries the same goal amount its CHILDREN
 *    carry, so letting the parent through double-counts the allocation.
 * 2. An [TxnLike.isTransfer] row (savings transfer / credit-card payment) is
 *    neither income nor spending, but its **goal allocation still counts** — an
 *    assigned savings transfer IS the savings signal. This is why `isTransfer`
 *    is deliberately separate from `excludeFromTotals` on the shared
 *    `Transaction`; do not fold the two together.
 * 3. Only a goal allocation strictly greater than zero contributes.
 *
 * Date parsing and account / context filtering stay on the platforms: they
 * depend on platform date APIs and on live UI selection state. What is shared
 * is eligibility plus the summing.
 */
object MonthlyTotalsAggregator {

    /** Minimum input a transaction has to provide to be aggregated. */
    interface TxnLike {
        /**
         * **Plaid convention: positive = expense, negative = income (CAD).**
         *
         * The same convention [ai.rules.BudgetHealthAggregator.TxnLike] uses, and
         * the only convention this object understands — every platform stores
         * something else and its adapter must normalise:
         * - Plaid / raw API rows are already in this convention; pass through.
         * - iOS and Android store `amount = -plaidAmount`; negate in the adapter.
         * - Web stores `abs(plaidAmount)` plus an `isIncome` flag; re-sign it.
         *
         * Never pass a magnitude and hope the caller sorts it out.
         */
        val amount: Double

        /** Calendar year of the transaction date, already parsed by the platform. */
        val year: Int

        /** Calendar month of the transaction date, 1 = January .. 12 = December. */
        val month: Int

        /** The shared `Transaction.excludeFromTotals`: split parent, distributed
         *  parent, or explicitly excluded. Rule 1 — contributes to nothing. */
        val excluded: Boolean

        /** The shared `Transaction.isTransfer` (`PlaidCategories.isTransfer`):
         *  internal money movement. Rule 2 — no income, no expense, but its goal
         *  allocation still counts. */
        val isTransfer: Boolean

        /** Dollars tagged onto this row as a savings-goal contribution, `0.0`
         *  when unassigned. Rule 3 — only a value `> 0` contributes. Non-null on
         *  purpose so Swift adapters don't have to box a `KotlinDouble?`. */
        val goalAllocation: Double

        /**
         * [goalAllocation] split per goal, empty when the row is unassigned.
         *
         * A single goal id would not do: one transaction can fund several goals,
         * and the transaction payload's `assigned_goal_id` is only the LARGEST of
         * them while `assigned_goal_amount` is the TOTAL — pairing those two
         * credits every dollar to the biggest goal. Adapters map
         * `assigned_goals`, and the values here must sum to [goalAllocation] or
         * the per-goal split and the month total will disagree.
         *
         * Only [goalAllocationsByGoal] reads it; month totals need the sum alone.
         */
        val goalAllocations: Map<String, Double>
    }

    /**
     * One month's totals. [income] and [expenses] are both POSITIVE magnitudes
     * (the sign convention is an input concern, not an output one) — that is what
     * all three dashboards feed into `MonthlyFinanceData`.
     */
    @Serializable
    data class MonthlyTotals(
        /** 1 = January .. 12 = December. */
        val month: Int,
        val income: Double = 0.0,
        val expenses: Double = 0.0,
        val goalAllocations: Double = 0.0,
    )

    /**
     * Totals for every month of [year], always 12 entries in calendar order
     * (January first), zero-filled for months with no eligible rows. Rows from
     * any other year are ignored.
     *
     * Single pass. Callers that need several years should use [aggregateByYear]
     * rather than calling this once per year.
     */
    fun aggregateYear(txns: List<TxnLike>, year: Int): List<MonthlyTotals> {
        val byMonth = mutableMapOf<Int, Accumulator>()
        for (txn in txns) {
            if (txn.year != year || !isEligible(txn)) continue
            accumulate(byMonth, txn)
        }
        return (1..12).map { month -> byMonth[month]?.toTotals(month) ?: MonthlyTotals(month) }
    }

    /**
     * Every year present in [txns], each as a zero-filled 12-month list — one
     * pass over the list total, for callers (web) that render several years off
     * a single cached scan.
     */
    fun aggregateByYear(txns: List<TxnLike>): Map<Int, List<MonthlyTotals>> {
        val byYear = mutableMapOf<Int, MutableMap<Int, Accumulator>>()
        for (txn in txns) {
            if (!isEligible(txn)) continue
            accumulate(byYear.getOrPut(txn.year) { mutableMapOf() }, txn)
        }
        return byYear.mapValues { (_, byMonth) ->
            (1..12).map { month -> byMonth[month]?.toTotals(month) ?: MonthlyTotals(month) }
        }
    }

    /** Totals for [year], or twelve zeroes when that year has no rows. */
    fun monthsOf(byYear: Map<Int, List<MonthlyTotals>>, year: Int): List<MonthlyTotals> =
        byYear[year] ?: (1..12).map { MonthlyTotals(it) }

    /**
     * What each goal actually received in [year]/[month], keyed by
     * [TxnLike.goalAllocations] — the per-goal split of the same figure [aggregateYear]
     * reports as [MonthlyTotals.goalAllocations], under the same three rules.
     *
     * Rule 2 matters most here: the money that funds a goal arrives as a
     * TRANSFER ("Transfer To Simplii No Fee" tagged to *Pay Mortgage early*), so
     * anything that filtered transfers out would report every goal as unfunded.
     *
     * Goals with nothing assigned are absent rather than zero — callers read it
     * with a `?: 0.0` — and a row carrying an allocation it cannot attribute to any
     * goal is skipped, since an unattributable allocation is not a per-goal number.
     *
     * A transaction funding SEVERAL goals contributes its own share to each, which
     * is why [TxnLike.goalAllocations] is a map and not a single id.
     */
    fun goalAllocationsByGoal(txns: List<TxnLike>, year: Int, month: Int): Map<String, Double> {
        val byGoal = mutableMapOf<String, Double>()
        for (txn in txns) {
            if (txn.year != year || txn.month != month || !isEligible(txn)) continue
            for ((goalId, amount) in txn.goalAllocations) {
                if (amount <= 0.0) continue
                byGoal[goalId] = (byGoal[goalId] ?: 0.0) + amount
            }
        }
        return byGoal
    }

    /**
     * Rule 1: split / distributed parents and explicitly excluded rows
     * contribute to nothing at all — including goal allocations. The single
     * gate every entry point runs a row through before summing anything.
     */
    private fun isEligible(txn: TxnLike): Boolean = !txn.excluded

    /** Rules 2 and 3, applied exactly once. */
    private fun accumulate(byMonth: MutableMap<Int, Accumulator>, txn: TxnLike) {
        val acc = byMonth.getOrPut(txn.month) { Accumulator() }

        // Rule 2: transfers skip the cash-flow buckets only.
        if (!txn.isTransfer) {
            if (txn.amount < 0.0) acc.income -= txn.amount   // negative = income
            else acc.expenses += txn.amount                  // positive = expense
        }

        // Rule 2 continued / rule 3: goal allocations count for transfers too.
        if (txn.goalAllocation > 0.0) acc.goalAllocations += txn.goalAllocation
    }

    private class Accumulator {
        var income: Double = 0.0
        var expenses: Double = 0.0
        var goalAllocations: Double = 0.0
        fun toTotals(month: Int) = MonthlyTotals(month, income, expenses, goalAllocations)
    }
}
